// =============================================================================
// SIREPPAR - Circuito "dado por NO PRESENTADO" (frontend)
// =============================================================================
// Espejo de src/validators/datosFormularioNoPresentado.ts del backend.
//
// Cuando vence el plazo de una intimación (admisibilidad) u observación
// (análisis) y el partido nunca subsanó, el expediente pasa a estado
// NO_PRESENTADO y arranca un circuito de resolución: Secretario → TEP → rechazo o
// vuelve a su etapa anterior. El estado NO_PRESENTADO es la etiqueta visible (cae
// en la columna Resolución de /gestion con badge rojo, así no se confunde con los
// expedientes correctos de esa etapa); la subfase vive en
// expediente.datosFormulario.noPresentado.
// =============================================================================

import { conEncabezadoInforme } from './informeEncabezado';

export type SubFaseNoPresentado = 'EN_SECRETARIO' | 'EN_TEP' | 'RESUELTO';
export type EstadoPrevioNoPresentado = 'INTIMADO' | 'OBSERVADO' | 'REC_PROVISORIO';

export interface NoPresentadoInfo {
  estadoPrevio: EstadoPrevioNoPresentado;
  subFase: SubFaseNoPresentado;
  elevadoEn?: string;
  elevadoPor?: string;
  elevadoAlTepEn?: string;
  elevadoAlTepPor?: string;
  // Qué resolvió el TEP. 'CADUCA' se conserva SÓLO para leer expedientes ya
  // resueltos antes de que la caducidad automática se eliminara del circuito: no
  // lo escribe nadie más. Descartarlo haría que un circuito cerrado —con su acta y
  // su fecha— se leyera como si nunca se hubiera resuelto.
  resolucion?: 'CADUCA' | 'RECHAZA' | 'APLAZA';
  acta?: string;
  resueltoEn?: string;
}

const SUBFASES: SubFaseNoPresentado[] = ['EN_SECRETARIO', 'EN_TEP', 'RESUELTO'];

/** Lee el circuito desde expediente.datosFormulario (o null si no hay/no es válido). */
export function getNoPresentado(datosFormulario: unknown): NoPresentadoInfo | null {
  if (!datosFormulario || typeof datosFormulario !== 'object' || Array.isArray(datosFormulario)) {
    return null;
  }
  const np = (datosFormulario as Record<string, unknown>).noPresentado;
  if (!np || typeof np !== 'object' || Array.isArray(np)) return null;
  const raw = np as Record<string, unknown>;
  if (typeof raw.subFase !== 'string' || !SUBFASES.includes(raw.subFase as SubFaseNoPresentado)) {
    return null;
  }
  if (raw.estadoPrevio !== 'INTIMADO' && raw.estadoPrevio !== 'OBSERVADO' && raw.estadoPrevio !== 'REC_PROVISORIO') {
    return null;
  }
  return raw as unknown as NoPresentadoInfo;
}

/** El expediente está esperando resolución del Secretario o del TEP. */
export function circuitoNoPresentadoAbierto(datosFormulario: unknown): boolean {
  const np = getNoPresentado(datosFormulario);
  return np !== null && (np.subFase === 'EN_SECRETARIO' || np.subFase === 'EN_TEP');
}

/**
 * Sufijo para el badge de estado: "No Presentado · En Secretario". Sin esto, dos
 * expedientes en NO_PRESENTADO se ven idénticos aunque uno esté esperando al
 * Secretario y el otro al TEP.
 */
export function sufijoBadgeNoPresentado(datosFormulario: unknown): string {
  const np = getNoPresentado(datosFormulario);
  if (!np) return '';
  switch (np.subFase) {
    case 'EN_SECRETARIO': return ' · En Secretario';
    case 'EN_TEP': return ' · En TEP';
    // El aplazamiento no deja `noPresentado` en el expediente (se archiva en el
    // historial), así que acá sólo llegan los cierres terminales. Se distinguen por
    // la resolución y no por el estado: los expedientes que el TEP hizo caducar
    // cuando la caducidad todavía era salida del circuito siguen diciendo lo suyo.
    case 'RESUELTO':
      if (np.resolucion === 'RECHAZA') return ' · Rechazo resuelto';
      return np.resolucion === 'CADUCA' ? ' · Caducidad resuelta' : '';
  }
}

// =============================================================================
// Textos precargados de los informes del circuito
// =============================================================================
// Son editables en el modal: acá va el esqueleto formal, y quien firma suma con
// los checks del panel lateral las intimaciones u observaciones concretas (bloque
// "Antecedentes" al final, ver historialInforme.ts). El editor es Markdown.

export interface DatosInformeNoPresentado {
  numero: string;
  estado: string;
  partido?: { denominacion?: string };
  observaciones?: Array<{ tipo?: string; estado?: string }>;
}

// Cuántas intimaciones/observaciones quedaron sin responder, para redactar en
// singular o plural sin que quede "1 observaciones".
const contarSinResponder = (exp: DatosInformeNoPresentado, esIntimacion: boolean) =>
  (exp.observaciones || []).filter(
    (o) => (esIntimacion ? o.tipo === 'INTIMACION' : o.tipo !== 'INTIMACION') && o.estado === 'PENDIENTE',
  ).length;

/** Informe de la supervisora al Secretario: se tiene por no presentada la solicitud. */
export function textoInformeNoPresentado(exp: DatosInformeNoPresentado): string {
  const esIntimacion = exp.estado === 'INTIMADO';
  const cantidad = contarSinResponder(exp, esIntimacion) || 1;
  const sustantivo = esIntimacion
    ? (cantidad === 1 ? 'la intimación cursada' : `las ${cantidad} intimaciones cursadas`)
    : (cantidad === 1 ? 'la observación formulada' : `las ${cantidad} observaciones formuladas`);
  const etapa = esIntimacion ? 'control de admisibilidad' : 'análisis';
  return conEncabezadoInforme(`Se informa al Señor Secretario Electoral que el partido **${exp.partido?.denominacion || ''}**, en el expediente **${exp.numero}**, **no ha subsanado en el plazo legal** ${sustantivo} en la etapa de ${etapa}, pese a encontrarse debidamente notificado.

Vencido el plazo otorgado sin que se diera cumplimiento a lo requerido, corresponde **dar por no presentada la solicitud** y elevar las presentes actuaciones a su consideración, a fin de que se resuelva lo que por derecho corresponda.`,
    { destinatario: 'SECRETARIO', nombrePartido: exp.partido?.denominacion });
}

/**
 * Informe de la supervisora al Secretario cuando el partido con reconocimiento
 * PROVISORIO no presentó el padrón de afiliados dentro de los 4 meses. No se
 * apoya en intimaciones/observaciones: lo que venció es el plazo legal del
 * provisorio, así que el texto habla del padrón y del plazo, no de subsanaciones.
 * Las observaciones que hubiera se suman igual desde el panel lateral.
 */
export function textoInformeNoPresentadoAfiliados(
  exp: DatosInformeNoPresentado & { plazoVence?: string | null },
): string {
  const vence = exp.plazoVence
    ? new Date(exp.plazoVence).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;
  const menciónPlazo = vence
    ? `cuyo plazo de cuatro (4) meses venció el **${vence}**`
    : 'cuyo plazo de cuatro (4) meses se encuentra vencido';
  return conEncabezadoInforme(`Se informa al Señor Secretario Electoral que el partido **${exp.partido?.denominacion || ''}**, en el expediente **${exp.numero}**, con **reconocimiento provisorio** otorgado por el Tribunal Electoral Provincial, **no ha presentado el padrón de afiliados** exigido por la Ley XII N° 23 —cuatro por mil (4‰) del padrón electoral—, ${menciónPlazo}.

Vencido el plazo legal sin que la agrupación diera cumplimiento a la presentación requerida, corresponde **tener por no presentada** la documentación y elevar las presentes actuaciones a su consideración, a fin de que se resuelva lo que por derecho corresponda.`,
    { destinatario: 'SECRETARIO', nombrePartido: exp.partido?.denominacion });
}

/**
 * Informe de la supervisora al Secretario cuando el partido con reconocimiento
 * PROVISORIO no presentó sus AUTORIDADES DEFINITIVAS. Puede habilitarse por dos
 * caminos: venció el plazo de 4 meses del provisorio, o se le observó algo de esta
 * etapa y venció ese plazo sin respuesta (bastante antes de los 4 meses). El texto
 * se adapta a cuál de los dos ocurrió.
 */
export function textoInformeNoPresentadoAutoridades(
  exp: DatosInformeNoPresentado & { plazoVence?: string | null; plazoCuatroMesesVencido?: boolean },
): string {
  const vence = exp.plazoVence
    ? new Date(exp.plazoVence).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;
  const motivo = exp.plazoCuatroMesesVencido
    ? (vence
        ? `no ha presentado sus autoridades partidarias definitivas** dentro del plazo de cuatro (4) meses previsto, que venció el **${vence}**`
        : 'no ha presentado sus autoridades partidarias definitivas** dentro del plazo de cuatro (4) meses previsto, que se encuentra vencido')
    : 'no ha dado cumplimiento a lo requerido respecto de sus autoridades partidarias definitivas** dentro del plazo otorgado, pese a encontrarse debidamente notificado';
  return conEncabezadoInforme(`Se informa al Señor Secretario Electoral que el partido **${exp.partido?.denominacion || ''}**, en el expediente **${exp.numero}**, con **reconocimiento provisorio** otorgado por el Tribunal Electoral Provincial, **${motivo}.

Vencido el plazo sin que la agrupación diera cumplimiento a la presentación requerida —convocatoria a elecciones internas y carga de las autoridades electas, con la Resolución de la Proclamación de Autoridades, el Acta de Última Asamblea y el Acta de Designación de Apoderados—, corresponde **tener por no presentada** la documentación y elevar las presentes actuaciones a su consideración, a fin de que se resuelva lo que por derecho corresponda.`,
    { destinatario: 'SECRETARIO', nombrePartido: exp.partido?.denominacion });
}

/**
 * Informe del Secretario al TEP, para que resuelva el rechazo o dé un nuevo plazo.
 *
 * NO le pide al Tribunal que resuelva la CADUCIDAD, aunque antes lo hacía: la caducidad
 * dejó de ser una salida registrable de este circuito y declararla es un acto aparte, que
 * se hace desde el panel de partidos. Pedir acá un acto que el sistema no puede asentar
 * dejaba al Secretario solicitando una resolución sin lugar donde volcarla.
 */
export function textoInformeTepNoPresentado(exp: DatosInformeNoPresentado): string {
  return conEncabezadoInforme(`Se elevan a consideración del Tribunal Electoral Provincial las presentes actuaciones correspondientes al partido **${exp.partido?.denominacion || ''}**, expediente **${exp.numero}**, en las que se tuvo por no presentada la solicitud por no haberse subsanado en el plazo legal las omisiones oportunamente señaladas.

Se solicita al Tribunal que resuelva lo que por derecho corresponda: el rechazo de la solicitud o, en su caso, el otorgamiento de un nuevo plazo al partido para dar cumplimiento a lo requerido.`,
    { destinatario: 'TEP', nombrePartido: exp.partido?.denominacion });
}

/** Etapa de la que viene el expediente, para textos de informes y avisos. */
export function etapaOrigenNoPresentado(datosFormulario: unknown): string {
  const np = getNoPresentado(datosFormulario);
  if (!np) return '';
  if (np.estadoPrevio === 'INTIMADO') return 'Admisibilidad (Intimado)';
  // El tramo del provisorio tiene DOS etapas (afiliados y autoridades definitivas)
  // y las dos comparten el estado REC_PROVISORIO, así que no se puede distinguir
  // cuál era desde acá: se rotula el tramo, no la etapa.
  if (np.estadoPrevio === 'REC_PROVISORIO') return 'Reconocimiento provisorio';
  return 'Análisis (Observado)';
}
